// /////////////////////////////////////////////////////////////
// [ファイル] SfaProjectsLayout.tsx
// [概要] /sfa/projects 配下 (一覧 / 新規登録 / 詳細) の親 Layout。旧画面の SalesProjectsLayout と同じ作り。
//        useSfaProjectsV2 をここで一度だけ呼び、子ルートが切り替わってもマウントされたままにして、
//        一覧の状態 (抽出条件・タイプ・検索ワード・並び順・ページ・表示件数・適用中の条件名・データ・スクロール位置) を保持する。
//        データ以外はタブ内 (sessionStorage) にも覚え、ほかのメニューへ移ったり再読み込みしたりしても残す。
//        表示列 (どの列をどの順で出すか) はブラウザ (localStorage) に残し、タブを閉じても同じ列で開く。
//        詳細・新規登録から一覧へ戻ったときは、同じ条件のまま取り直して編集結果を反映する。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../firebase/PermissionProvider';
import { useAuth } from '../../firebase/AuthProvider';
import { SfaProjectsListContext } from '../../providers/SfaProjectsListProvider';
import { useSfaProjectsV2, listStateKey, listScrollKey, loadListScroll, SfaListFilters, DEFAULT_LIST_FILTERS, DEFAULT_LIST_SORT } from '../../hooks/useSfaStructure';
import { loadListColumns, normalizeListColumns, saveListColumns } from '../../components/sfa/sfaListColumns';

const LIST_PATH = '/sfa/projects';

export default function SfaProjectsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { permissionData, loading: permsLoading } = usePermissions();
  const permissions = permsLoading ? null : permissionData;
  // 閲覧範囲は旧画面と同じ判定 (全件 / 支店 / 個人)
  const viewLevel = !permissions ? 'individual' : permissions.includes('readdata.sfa.all_view') ? 'all' : permissions.includes('readdata.sfa.branch_view') ? 'branch' : 'individual';

  // 詳細や新規登録を直接開いたときは一覧を取らず、初めて一覧を表示したときに取る
  const [listShown, setListShown] = useState(location.pathname === LIST_PATH);

  // 案件条件リストから保存済み条件を適用して遷移してきた場合は、覚えている状態よりその条件を優先する
  // (案件条件リストは配下の外なので、そこから来るときは必ず本 Layout が新しくマウントされる)
  const applied = location.state?.appliedFilters as Partial<SfaListFilters> | undefined;
  const list = useSfaProjectsV2({
    viewLevel,
    userEmail: user?.email || undefined,
    ready: permissions !== null && !!user?.email && listShown,
    initialFilters: applied ? { ...DEFAULT_LIST_FILTERS, ...applied } : undefined,
    initialSort: location.state?.appliedSort || DEFAULT_LIST_SORT,
    initialFilterName: location.state?.appliedFilterName || null,
    storageKey: user?.email ? listStateKey(user.email) : undefined,
  });

  // 一覧テーブルのスクロール位置。タブ内に覚えた位置から始める (案件条件リストから条件を適用して来たときは先頭)
  const scrollStorageKey = user?.email ? listScrollKey(user.email) : undefined;
  const [initialScroll] = useState(() => (applied ? 0 : loadListScroll(scrollStorageKey)));
  const scrollPositionRef = useRef<number>(initialScroll);

  // 表示列。保存した条件から適用して来たときはその列、それ以外は利用者がブラウザに残した設定（無ければ既定）。
  // 変えるたびにブラウザへ残す（タブを閉じても消えない）
  const email = user?.email || undefined;
  const [columns, setColumnsState] = useState<string[]>(() => (
    location.state?.appliedColumns ? normalizeListColumns(location.state.appliedColumns) : loadListColumns(email)
  ));
  const setColumns = (cols: string[]) => { const n = normalizeListColumns(cols); setColumnsState(n); saveListColumns(email, n); };
  useEffect(() => { if (location.state?.appliedColumns) saveListColumns(email, normalizeListColumns(location.state.appliedColumns)); }, [location.state, email]);

  // 反映した条件は state から消し、ブラウザの戻る等で同じ条件が再び適用されないようにする
  useEffect(() => {
    if (location.state?.appliedFilters) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // 詳細・新規登録から一覧へ戻ったときは、同じ条件のまま取り直す (詳細での編集を一覧へ反映する)
  const reloadRef = useRef(list.reload);
  reloadRef.current = list.reload;
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const from = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (location.pathname !== LIST_PATH || from === LIST_PATH) return;
    if (listShown) reloadRef.current();
    else setListShown(true); // 初めて一覧を表示する (取得はフック側の初回取得に任せる)
  }, [location.pathname, listShown]);

  return (
    <SfaProjectsListContext.Provider value={{ ...list, viewLevel, permissions, scrollPositionRef, scrollStorageKey, columns, setColumns }}>
      <Outlet />
    </SfaProjectsListContext.Provider>
  );
}
